import {Component} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {NavigationEnd, Router} from '@angular/router';
import {MenuItemI} from './menu.item.metadata';
import {MenuResizeService} from './resizer/menu.resizer.service';
import {Subscription} from 'rxjs';
import {MENU} from './menu.definition';
import {Roles, RoleService} from "../../../core/security";
import {isNullOrUndefined} from "util";
import {BOHttpClientInterceptor} from "../../../core/http/client/bo.http.interceptor";

/** Counter of a menu item: where to read it, what it means and where its click goes. */
interface MenuCounterI {
    url: string;
    field: string;
    title: string;
    // Optional list in the response with the number per group, shown in the tooltip ({name, count} fields)
    detail?: {field: string, nameField: string, idField: string, countField: string};
    queryParams: {[key: string]: string};
    // Window event that asks for a refresh (sent by the page that changes the number)
    refreshEvent: string;
}

@Component({
    selector: 'menu-c',
    template: require('./menu.template.html')
})
export class Menu {

    private static readonly COUNTERS: {[key: string]: MenuCounterI} = {
        // Same values as FAILED_PAID_VIEW / FAILED_PAID_CHANGED_EVENT of the Open Banking payments page
        obFailedPaid: {
            url: '/api/backoffice/open-banking/failed-then-paid/count',
            field: 'pending',
            title: 'payments reported as failed and paid afterwards: client not notified yet',
            detail: {field: 'byClient', nameField: 'clientName', idField: 'clientId', countField: 'pending'},
            queryParams: {view: 'failed-then-paid'},
            refreshEvent: 'ob-failed-paid-changed'
        }
    };
    // No timer: a periodic request would renew the BO token and the session would never expire for inactivity.
    // Counters reload on navigation (at most once per minute) and when their page changes them.
    private static readonly COUNTER_MIN_INTERVAL_MS = 60 * 1000;

    menu: MenuItemI[] = MENU;
    menuUpdatedFromPermissions: MenuItemI[] = [];
    counters: {[key: string]: number} = {};
    private counterDetails: {[key: string]: string} = {};
    private isLargeMenuView: boolean = true;
    private resizeSubscription: Subscription;
    private counterLoadedAt: {[key: string]: number} = {};
    private navigationSubscription: Subscription;
    private counterListeners: {event: string, handler: () => void}[] = [];

    constructor(private router: Router, private menuResizeService: MenuResizeService, private roleService: RoleService,
                private httpClient: HttpClient) {
    }

    ngOnInit() {
        this.updateMenuFromPermissions();
        this.startCounters();
        // handle change route event
        this.router.events.subscribe(() => {
            this.updateMenuActiveState();
        });

        // handle change menu size event
        this.resizeSubscription = this.menuResizeService.onResizeMenuObserver()
            .subscribe((isLarge: boolean) => this.updateMenuSize(isLarge));

        // call update on component init
        setTimeout(() => this.updateMenuActiveState());
    }

    private updateMenuFromPermissions(): void {

        const permissionRoutes = ([] as string[]).concat.apply([], this.roleService.getPermissions().map(permission => {
            const route = permission.route.split(':')[0].substring(1);
            if (route.endsWith('/')) {
                return route.slice(0, -1);
            }
            return route;
        }));

        this.menuUpdatedFromPermissions = this.filterMenuItems(this.menu, permissionRoutes);
    }

    private filterMenuItems(menuItems: MenuItemI[], allowedPaths: any): MenuItemI[] {
        return menuItems.filter(menuItem => {
            if (!Array.isArray(menuItem.path[0])) {
                const hasAllowedPath = allowedPaths.includes(menuItem.path.join('/'));
                if (hasAllowedPath && menuItem.children) {
                    menuItem.children = this.filterMenuItems(menuItem.children, allowedPaths);
                    return true;
                }
                return hasAllowedPath;
            } else {
                let hasAllowedPath = false;
                for (const path of menuItem.path) {
                    const joinedPath = Array.isArray(path) ? path.join('/') : path;
                    if (allowedPaths.includes(joinedPath)) {
                        hasAllowedPath = true;
                        break;
                    }
                }

                if (hasAllowedPath && menuItem.children) {
                    menuItem.children = this.filterMenuItems(menuItem.children, allowedPaths);
                    return true;
                }
                return hasAllowedPath;
            }
        });
    }

    ngOnDestroy() {
        this.resizeSubscription.unsubscribe();
        if (this.navigationSubscription) {
            this.navigationSubscription.unsubscribe();
        }
        this.counterListeners.forEach(l => window.removeEventListener(l.event, l.handler));
    }

    /** Counters of the visible menu items: loaded now, on navigation and when their page changes them. */
    private startCounters(): void {
        const keys: string[] = [];
        this.menuUpdatedFromPermissions.forEach(item => (item.children || [])
            .filter(child => child.counter && Menu.COUNTERS[child.counter] && !this.isDisabled(child))
            .forEach(child => keys.indexOf(child.counter) < 0 && keys.push(child.counter)));
        if (!keys.length) {
            return;
        }
        keys.forEach(key => {
            const handler = () => this.loadCounter(key);
            window.addEventListener(Menu.COUNTERS[key].refreshEvent, handler);
            this.counterListeners.push({event: Menu.COUNTERS[key].refreshEvent, handler: handler});
            this.loadCounter(key);
        });
        this.navigationSubscription = this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                keys.filter(key => Date.now() - (this.counterLoadedAt[key] || 0) >= Menu.COUNTER_MIN_INTERVAL_MS)
                    .forEach(key => this.loadCounter(key));
            }
        });
    }

    private loadCounter(key: string): void {
        const counter = Menu.COUNTERS[key];
        this.counterLoadedAt[key] = Date.now();
        // A 403 (no role for the counted list) must not leave the current page
        const headers = new HttpHeaders().set(BOHttpClientInterceptor.NO_FORBIDDEN_REDIRECT_HEADER, 'true');
        this.httpClient.get<any>(counter.url, {headers: headers}).subscribe(
            body => {
                this.counters[key] = (body && +body[counter.field]) || 0;
                this.counterDetails[key] = Menu.counterDetail(counter, body);
            },
            () => {
                this.counters[key] = 0;
                this.counterDetails[key] = '';
            });
    }

    /** "CLIENT A 3, #125 1" from the per-group list of the counter response. */
    private static counterDetail(counter: MenuCounterI, body: any): string {
        const rows = counter.detail && body && body[counter.detail.field];
        if (!Array.isArray(rows)) {
            return '';
        }
        return rows.map(row => (row[counter.detail.nameField] || ('#' + row[counter.detail.idField]))
            + ' ' + row[counter.detail.countField]).join(', ');
    }

    counterValue(item: MenuItemI): number {
        return item.counter ? (this.counters[item.counter] || 0) : 0;
    }

    counterTitle(item: MenuItemI): string {
        const counter = item.counter && Menu.COUNTERS[item.counter];
        if (!counter) {
            return '';
        }
        const detail = this.counterDetails[item.counter];
        return this.counterValue(item) + ' ' + counter.title + (detail ? ' (' + detail + ')' : '');
    }

    /** Sum of the children counters, shown on the section title. */
    childrenCount(item: MenuItemI): number {
        return (item.children || []).reduce((sum, child) => sum + this.counterValue(child), 0);
    }

    childrenCountTitle(item: MenuItemI): string {
        return (item.children || []).filter(child => this.counterValue(child) > 0)
            .map(child => this.counterTitle(child)).join('; ');
    }

    /** The counter opens the list it counts. */
    openCounter(item: MenuItemI, event: Event): void {
        event.stopPropagation();
        const counter = item.counter && Menu.COUNTERS[item.counter];
        if (!counter || this.isDisabled(item)) {
            return;
        }
        this.router.navigate(item.path as string[], {queryParams: counter.queryParams});
        setTimeout(() => this.updateMenuActiveState(), 1);
    }

    getMenuItemClasses(item: MenuItemI): any {
        if (this.hasActiveChildren(item)) {
            // menu item it has the active child
            if (this.hasAnotherActive(item)) {
                // another root item been selected by user, hide current
                return {active: false, open: false}
            } else {
                // activate current, because it has the active child
                return {active: true, open: item.active}
            }
        } else {
            // menu item do not have the active child, activate if selected
            return {active: item.active, open: item.active}
        }
    }

    getMenuItemAnchorClasses(item: MenuItemI): any {
        return {
            'active       ': this.isActive(item),
            'disabled-item': this.isDisabled(item)
        }
    }

    toggleMenuItem(item: MenuItemI): void {
        if (!this.isDisabled(item)) {
            for (let menu_item of this.menuUpdatedFromPermissions) {
                // toggle for current, false to another
                menu_item.active = menu_item === item && !menu_item.active;
            }
        }
    }

    handleMenuItemClick(item: MenuItemI): void {
        console.log('[Menu] handleMenuItemClick called with item:', item);
        console.log('[Menu] isDisabled:', this.isDisabled(item));

        if (!this.isDisabled(item)) {
            let path = item.path;
            console.log('[Menu] Original path:', path);

            if(item.path.length > 0 && Array.isArray(item.path[0])) {
                console.log('[Menu] Path is array of arrays, finding allowed path...');
                path = (item.path as Array<Array<string>>).find(path => {
                    const route = '/' + path.join('/');
                    const hasPermission = this.hasPathPermissions(item.roles,route);
                    console.log('[Menu] Checking path:', path, 'route:', route, 'hasPermission:', hasPermission);
                    return hasPermission;
                })
                console.log('[Menu] Selected path:', path);
            }

            if (path) {
                console.log('[Menu] Navigating to:', path);
                this.router.navigate(path);
                setTimeout(() => this.updateMenuActiveState(), 1);
            } else {
                console.error('[Menu] No valid path found for navigation!');
            }
        } else {
            console.warn('[Menu] Menu item is disabled, cannot navigate');
        }
    }

    private updateMenuActiveState(): void {
        for (let item of this.menuUpdatedFromPermissions) {
            item.active = this.isLargeMenuView && this.hasActiveChildren(item);
        }
    }

    private updateMenuSize(isLarge: boolean): void {
        this.isLargeMenuView = isLarge;
        this.updateMenuActiveState()
    }

    private isActive(item: MenuItemI): boolean {
        try {
            if(item.path.length > 0 && Array.isArray(item.path[0])){
                return (item.path as Array<Array<string>>).some((path: Array<string>) => {
                    return this.router.isActive(this.router.createUrlTree(path), false);
                });
            } else {
                return this.router.isActive(this.router.createUrlTree(item.path), false)
            }
        } catch (err) {
            // can throw exception if route not found
            return false;
        }
    }

    private hasActiveChildren(current: MenuItemI): boolean {
        for (let item of current.children) {
            if (this.isActive(item)) {
                return true
            }
        }

        return false
    }

    private hasAnotherActive(current: MenuItemI): boolean {
        for (let item of this.menuUpdatedFromPermissions) {
            if (current !== item && item.active) {
                return true
            }
        }

        return false
    }

    isDisabled(item: MenuItemI): boolean {
        if(item.disabled){
            console.log('[Menu] Item is explicitly disabled:', item.id);
            return true;
        }

        if(item.path.length > 0 && Array.isArray(item.path[0])) {
            const isDisabled = (item.path as Array<Array<string>>).every(path => {
                const route = '/' + path.join('/');
                const hasPermission = this.hasPathPermissions(item.roles, route);
                console.log('[Menu] Checking permission for path:', path, 'route:', route, 'hasPermission:', hasPermission);
                return !hasPermission;
            });
            console.log('[Menu] Item', item.id, 'isDisabled:', isDisabled);
            return isDisabled;
        } else{
            const route = '/' + item.path.join('/');
            const hasPermission = this.hasPathPermissions(item.roles, route);
            console.log('[Menu] Item', item.id, 'route:', route, 'hasPermission:', hasPermission, 'isDisabled:', !hasPermission);
            return !hasPermission;
        }
    }

    hasPathPermissions(roles: Roles | Array<Roles>, route: string): boolean {
        if(isNullOrUndefined(roles)) {
            return this.roleService.hasMenuAccess(route);
        } else {
            return this.roleService.hasMenuRoles(roles, route);
        }
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/modules/space/menu/menu.component.ts